'use client';

import { ChevronDown, ChevronUp } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

export interface ShowMoreProps {
  children: ReactNode;
  showMoreText?: string;
  showLessText?: string;
  maxContentHeight?: number;
  hideBottomGradient?: boolean;
  isDefaultExpanded?: boolean;
}

export function ShowMore({
  children,
  hideBottomGradient = false,
  isDefaultExpanded = false,
  maxContentHeight = 150,
  showMoreText = 'Show More',
  showLessText = 'Show Less',
}: ShowMoreProps) {
  const idSuffix = useId();
  const toggleId = `checkbox-toggle-${idSuffix}`;
  const contentId = `show-more-content-${idSuffix}`;
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded);

  return (
    <div className="relative">
      <input
        className="peer hidden"
        defaultChecked={isDefaultExpanded}
        id={toggleId}
        onChange={event => setIsExpanded(event.target.checked)}
        type="checkbox"
      />
      <div
        className={`relative mb-4 overflow-y-hidden text-inherit peer-checked:!max-h-none ${hideBottomGradient ? '' : "after:absolute after:bottom-0 after:block after:h-1/2 after:w-full after:bg-gradient-to-b after:from-transparent after:to-background after:content-[''] peer-checked:after:hidden"}`}
        id={contentId}
        role="region"
        style={{ maxHeight: maxContentHeight }}
      >
        {children}
      </div>
      {/* CSS cannot detect overflow; NWA-3998 accepts an always-visible control for short content. */}
      <button
        aria-controls={contentId}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? showLessText : showMoreText}
        className="block max-w-fit cursor-pointer border-0 bg-transparent p-0 peer-checked:[&_.show-less]:flex peer-checked:[&_.show-more]:hidden"
        onClick={() => document.getElementById(toggleId)?.click()}
        type="button"
      >
        <span className="show-more inline-flex items-center gap-1 typography-text-small font-bold uppercase text-secondary-foreground hover:underline decoration-secondary-foreground">
          {showMoreText}
          <ChevronDown aria-hidden className="size-[13px]" />
        </span>
        <span className="show-less hidden items-center gap-1 typography-text-small font-bold uppercase text-secondary-foreground hover:underline decoration-secondary-foreground">
          {showLessText}
          <ChevronUp aria-hidden className="size-[13px]" />
        </span>
      </button>
    </div>
  );
}
